import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight, Menu, Search, X } from "lucide-react";

// images
import { ReactComponent as Home } from "../../assets/images/Home.svg";
import { ReactComponent as Smartphone } from "../../assets/images/Devices.svg";
import { ReactComponent as Fix } from "../../assets/images/fix.svg";
import { ReactComponent as Banknote } from "../../assets/images/Banknote.svg";
import { ReactComponent as Sett } from "../../assets/images/Settings.svg";
import { ReactComponent as Siderbar } from "../../assets/images/Siderbar.svg";
import { ReactComponent as Graph } from "../../assets/images/graph.svg";

const APP_BASE = "/app";

function appPath(p) {
  if (!p) return p;
  // ถ้าใส่ /app มาแล้ว ไม่ต้องเติม
  if (p.startsWith(APP_BASE)) return p;
  // กันพวก public เช่น / หรือ /products ไม่ต้องเข้ามา sidebar อยู่แล้ว
  return `${APP_BASE}${p.startsWith("/") ? "" : "/"}${p}`;
}

const W_FULL = 280;
const W_COLLAPSED = 88;

function cx(...arr) {
  return arr.filter(Boolean).join(" ");
}

const titles = {
  "/app/main": "Dashboard",
  "/app/pricing-queue": "รายการสินค้ารอกำหนดราคา",
  "/app/accessory-history": "ประวัตินำเข้า/ขายอุปกรณ์เสริม",
  "/app/products/stock": "สต๊อกสินค้า",
  "/app/repairs": "การซ่อม",
  "/app/pawn": "จำนำ",

  "/app/settings/master/main-categories": "Master Data • ประเภทสินค้าหลัก",
  "/app/settings/master/accessory-categories": "Master Data • ประเภทอุปกรณ์เสริม",
  "/app/settings/master/equipment": "Master Data • อุปกรณ์",
  "/app/settings/master/quality": "Master Data • คุณภาพสินค้า",
  "/app/settings/master/warranty": "Master Data • ประกัน",
  "/app/settings/master/contacts": "Master Data • ช่องทางการติดต่อ",
  // "/app/settings/master/models": "Master Data • รุ่นสินค้า",
  // "/app/settings/master/capacities": "Master Data • ความจุ",
  // "/app/settings/master/colors": "Master Data • สี",
  "/app/settings/profile": "โปรไฟล์",
};

export default function Sidebar({
  collapsed,
  setCollapsed,
  mobileOpen,
  setMobileOpen,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const title = titles[location.pathname] || "Backoffice";
  const roleName = String(localStorage.getItem("auth_role_name") || "").trim().toLowerCase();
  const isAdminRole = roleName === "admin";

  const menus = useMemo(
    () => [
      {
        key: "dashboard",
        label: "Dashboard",
        icon: Graph,
        path: appPath("/main"),
      },
      {
        key: "home",
        label: "หน้าหลัก",
        icon: Home,
        children: [
          { key: "pricing-queue", label: "รอกำหนดราคา", path: appPath("/pricing-queue") },
          {
            key: "accessory-history",
            label: "ประวัตินำเข้า/ขายอุปกรณ์เสริม",
            path: appPath("/accessory-history"),
          },
        ],
      },
      { key: "products", label: "สต๊อกสินค้า", icon: Smartphone, path: appPath("/products/stock") },
      // {
      //   key: "products",
      //   label: "รายการสินค้า",
      //   icon: Smartphone,
      //   children: [
      //     { key: "products-stock", label: "สต๊อกสินค้า", path: appPath("/products/stock") },
      //   ],
      // },
      { key: "repairs", label: "การซ่อม", icon: Fix, path: appPath("/repairs") },
      { key: "pawn", label: "จำนำ", icon: Banknote, path: appPath("/pawn") },
      {
        key: "settings",
        label: "ตั้งค่า",
        icon: Sett,
        children: [
          ...(!isAdminRole
            ? [
                {
                  key: "master-data",
                  label: "Master Data",
                  children: [
                    { key: "main-categories", label: "ประเภทสินค้าหลัก", path: appPath("/settings/master/main-categories") },
                    { key: "accessory-categories", label: "ประเภทอุปกรณ์เสริม", path: appPath("/settings/master/accessory-categories") },
                    { key: "devices", label: "อุปกรณ์", path: appPath("/settings/master/equipment") },
                    { key: "quality", label: "คุณภาพสินค้า", path: appPath("/settings/master/quality") },
                    { key: "warranty", label: "ประกัน", path: appPath("/settings/master/warranty") },
                    { key: "contacts", label: "ช่องทางการติดต่อ", path: appPath("/settings/master/contact-infos") },
                    // { key: "models", label: "รุ่นสินค้า", path: appPath("/settings/master/models") },
                    // { key: "capacities", label: "ความจุ", path: appPath("/settings/master/capacities") },
                    // { key: "colors", label: "สี", path: appPath("/settings/master/colors") },
                  ],
                },
              ]
            : []),
          { key: "profile", label: "โปรไฟล์", icon: Sett, path: appPath("/settings/profile") },
        ],
      },
    ],
    [isAdminRole]
  );


  const isActiveAny = (node) => {
    if (node.path && node.path === location.pathname) return true;
    return (node.children || []).some(isActiveAny);
  };

  // ✅ สร้าง openKeys จากเส้นทางปัจจุบัน (เปิดเฉพาะ parent ที่เกี่ยวข้อง)
  const buildOpenKeys = (pathname) => {
    const keys = new Set();
    const walk = (node, parents = []) => {
      if (node.path === pathname) parents.forEach((k) => keys.add(k));
      (node.children || []).forEach((c) => walk(c, [...parents, node.key]));
    };
    menus.forEach((m) => walk(m, []));
    return keys;
  };

  const [openKeys, setOpenKeys] = useState(() => buildOpenKeys(location.pathname));

  // ✅ เปลี่ยนหน้าแล้วให้ sidebar เปิดเฉพาะเมนูที่เกี่ยวข้องกับหน้านั้น
  useEffect(() => {
    setOpenKeys(buildOpenKeys(location.pathname));
  }, [location.pathname]); // menus stable จาก useMemo([])

  // ✅ toggle ปกติ (สำหรับเมนูย่อย)
  const toggleKey = (key) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ✅ Accordion สำหรับเมนูใหญ่: เปิดได้ทีละอัน (กดซ้ำ = ปิด)
  const toggleRootKey = (key) => {
    setOpenKeys((prev) => {
      const isOpen = prev.has(key);
      const next = new Set();
      if (!isOpen) next.add(key); // เปิดตัวที่กด และปิดตัวอื่นทั้งหมด
      return next;
    });
  };

  const [accountOpen, setAccountOpen] = useState(false);

  const accountName = useMemo(() => {
    try {
      const raw = localStorage.getItem("auth_user"); // key ที่เราเซฟตอน login
      const u = raw ? JSON.parse(raw) : null;
      return u?.username || u?.email || "Admin";
    } catch {
      return "Admin";
    }
  }, []);

  const onLogout = () => {
    // ล้างทุกอย่างที่เกี่ยวกับ auth
    localStorage.removeItem("jwt");
    localStorage.removeItem("auth_user");
    localStorage.removeItem("auth_role_name");
    localStorage.removeItem("USER_ID");
    localStorage.removeItem("token"); // เผื่อมีของเก่า

    // ถ้าคุณใช้ Cookies เก็บ jwt ด้วย
    // (ปรับชื่อ cookie ให้ตรงกับที่คุณตั้งไว้)
    // Cookies.remove("bestphone_jwt", { path: "/" });

    setAccountOpen(false);
    setMobileOpen(false);
    navigate("/login", { replace: true });
  };



  function HeaderBar({ mode = "page" }) {
    return (
      <div
        className={cx(
          "sticky top-0 z-30 border-b border-slate-200 bg-white",
          mode === "drawer" && "rounded-t-2xl"
        )}
      >
        <div className="px-4 md:px-5 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => setMobileOpen(mode === "drawer" ? false : true)}
              className="md:hidden h-10 w-10 inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white active:scale-[0.98]"
            >
              {mode === "drawer" ? <X size={18} /> : <Menu size={18} />}
            </button>

            <div className="min-w-0">
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18 }}
                className="text-xl md:text-2xl font-extrabold truncate"
              >
                {title}
              </motion.div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-white">
              <Search size={16} className="text-[#1D161680]" />
              <input
                className="outline-none border-none bg-transparent text-sm w-[260px] placeholder:text-[#1D161680] text-[#1D161680]"
                placeholder="ค้นหารายการ..."
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderNode = (node, depth = 0) => {
    const Icon = node.icon;
    const active = isActiveAny(node);
    const hasChildren = (node.children || []).length > 0;
    const isOpen = openKeys.has(node.key);

    const padLeft = depth === 0 ? "px-4" : "px-3";
    const rowBase = cx(
      "relative flex items-center gap-3 py-3 cursor-pointer transition rounded-xl",
      padLeft,
      active ? "bg-blue-50 text-base" : "text-dark-bg hover:bg-slate-900/5",
      collapsed ? "justify-center px-2" : "justify-start"
    );

    const onClick = () => {
      if (hasChildren) {
        // ✅ เมนูใหญ่: กดแล้วต้องหุบเมนูใหญ่อื่น
        if (depth === 0) toggleRootKey(node.key);
        else toggleKey(node.key);
        return;
      }

      if (node.path) {
        // ✅ ถ้าเป็นเมนูใหญ่ที่เป็น path (เช่น repairs/pawn) ให้หุบเมนูอื่นด้วย
        if (depth === 0) setOpenKeys(new Set());
        navigate(node.path);
        setMobileOpen(false);
      }
    };

    return (
      <div key={node.key} className={cx(depth === 0 ? "mb-2" : "mb-1")}>
        <div className={rowBase} onClick={onClick}>
          {Icon ? (
            // svg component ส่วนใหญ่ใช้ width/height
            <Icon
              width={22}
              height={22}
              className={active ? "text-base" : "text-dark-bg"}
            />
          ) : null}

          <AnimatePresence initial={false}>
            {!collapsed && (
              <motion.div
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -6 }}
                className="flex-1 min-w-0"
              >
                <div className="text-md font-medium truncate">{node.label}</div>
              </motion.div>
            )}
          </AnimatePresence>

          {!collapsed && node.path && <NavLink to={node.path} className="hidden" />}

          {active && <span className="absolute right-0 top-0 bottom-0 w-[3px] bg-base" />}
        </div>

        <AnimatePresence initial={false}>
          {/* ✅ สำคัญ: เอา (|| active) ออก เพื่อให้หุบได้จริงเมื่อกดเมนูใหญ่อื่น */}
          {!collapsed && hasChildren && isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.18 }}
              className={cx("relative overflow-hidden", depth === 0 ? "ml-6 mt-2" : "ml-6")}
            >
              <span className="absolute left-2 top-2 bottom-2 w-[3px] rounded-full bg-slate-200" />
              <div className="pl-6 space-y-1">
                {node.children.map((c) => {
                  if (c.path) {
                    return (
                      <NavLink
                        key={c.key}
                        to={c.path}
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) =>
                          cx(
                            "flex items-center justify-between rounded-xl px-3 py-2 text-md font-medium transition",
                            isActive ? "text-base" : "text-dark-bg hover:bg-slate-900/5"
                          )
                        }
                      >
                        <span className="truncate">{c.label}</span>
                        {location.pathname === c.path && (
                          <ChevronRight className="text-base" size={20} />
                        )}
                      </NavLink>
                    );
                  }

                  return renderNode(c, depth + 1);
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const SidebarShell = (
    <motion.aside
      animate={{ width: collapsed ? W_COLLAPSED : W_FULL }}
      transition={{ type: "spring", stiffness: 260, damping: 28 }}
      className="h-full bg-white border-r border-slate-200 flex flex-col"
    >
      {/* Brand */}
      <div className={cx("border-b border-slate-200 bg-white", collapsed ? "px-2" : "px-3")}>
        <div className={cx("py-4 flex items-center", collapsed ? "justify-center" : "justify-between")}>
          {!collapsed && <div className="text-2xl tracking-tight truncate font-semibold">BestPhone</div>}

          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="hidden md:inline-flex items-center justify-center"
            title="ย่อ/ขยาย"
          >
            <Siderbar width={18} height={18} />
          </button>
        </div>
      </div>

      <div className="px-5 pt-4 pb-2 text-sm font-medium text-slate-400">
        <AnimatePresence initial={false}>
          {!collapsed && (
            <motion.div initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -6 }}>
              เมนูหลัก
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="pb-3 overflow-y-auto px-3">{menus.map((m) => renderNode(m, 0))}</div>

      <div className="mt-auto">
        <div className="px-5 pb-2 text-sm font-medium text-slate-400">
          <AnimatePresence initial={false}>
            {!collapsed && (
              <motion.div initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -6 }}>
                บัญชี
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="px-3 pb-4">
          <div className="relative">
            <div
              onClick={() => setAccountOpen((v) => !v)}
              className={cx(
                "flex items-center gap-3 rounded-xl bg-[#F5F5F5] px-4 py-3 cursor-pointer select-none",
                collapsed && "justify-center px-2"
              )}
            >
              <AnimatePresence initial={false}>
                {!collapsed && (
                  <motion.div
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -6 }}
                    className="flex-1 min-w-0"
                  >
                    <div className="text-md font-medium truncate">{accountName}</div>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence initial={false}>
                {!collapsed && (
                  <motion.div
                    initial={{ opacity: 0, rotate: -90 }}
                    animate={{ opacity: 1, rotate: accountOpen ? 90 : 0 }}
                    exit={{ opacity: 0 }}
                    className="text-slate-500"
                  >
                    <ChevronRight size={20} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* ✅ Logout popover ยื่นออกไปด้านขวา */}
            <AnimatePresence>
              {accountOpen && !collapsed && (
                <motion.div
                  initial={{ opacity: 0, x: -8, scale: 0.98 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: -8, scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-1 -translate-y-1/2 left-full ml-3 z-50"
                >
                  <div className="w-40 rounded-xl bg-white shadow-xl border border-slate-200 overflow-hidden">
                    <button
                      type="button"
                      onClick={onLogout}
                      className="w-full px-4 py-3 text-left text-sm font-semibold text-rose-600 hover:bg-rose-50"
                    >
                      Logout
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

      </div>
    </motion.aside>
  );

  return (
    <>
      {/* mobile top header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30">
        <HeaderBar mode="page" />
      </div>

      {/* desktop sidebar */}
      <div className="hidden md:block h-screen">{SidebarShell}</div>

      {/* mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-slate-900/40 z-40 md:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              className="fixed left-0 top-0 z-50 md:hidden h-screen"
              initial={{ x: -320 }}
              animate={{ x: 0 }}
              exit={{ x: -320 }}
              transition={{ type: "spring", stiffness: 260, damping: 28 }}
            >
              <div className="w-[280px] h-full bg-white shadow-2xl flex flex-col">
                <HeaderBar mode="drawer" />
                <div className="flex-1 min-h-0">{SidebarShell}</div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
